import React from 'react';

interface Props {
    values: number[];
    width?: number;
    height?: number;
    strokeColor?: string;
    fillColor?: string;
    strokeWidth?: number;
}

/**
 * Tiny SVG sparkline. Uses viewBox so it scales to its container width.
 * Always renders a fill gradient + line. Won't render if fewer than 2 points.
 */
const Sparkline: React.FC<Props> = ({
    values,
    width = 220,
    height = 44,
    strokeColor = 'var(--av2-accent)',
    fillColor = 'rgba(30, 58, 95, 0.10)',
    strokeWidth = 2,
}) => {
    if (!values || values.length < 2) {
        return <svg width={width} height={height} aria-hidden="true" />;
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const padding = strokeWidth + 1;
    const innerW = width - padding * 2;
    const innerH = height - padding * 2;

    const points = values.map((v, i) => {
        const x = padding + (i / (values.length - 1)) * innerW;
        const y = padding + innerH - ((v - min) / range) * innerH;
        return { x, y };
    });

    const linePath = points
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
        .join(' ');
    const last = points[points.length - 1];
    const first = points[0];
    const areaPath = `${linePath} L ${last.x.toFixed(2)} ${(height - padding).toFixed(2)} L ${first.x.toFixed(2)} ${(height - padding).toFixed(2)} Z`;

    return (
        <svg
            className="av2-sparkline"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <path d={areaPath} fill={fillColor} />
            <path
                d={linePath}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            <circle cx={last.x} cy={last.y} r={3} fill={strokeColor} />
        </svg>
    );
};

export default Sparkline;
